/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {cloneDeep} from 'lodash';
import {toAccountIdMap} from '@ion/core/src/entities/Accounts';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink} from '@ion/core/src/types/entities';
import {sortNestedAccounts} from '@ion/core/src/utils/SortUtils';
import {
  GLOBAL_GROUP_INITIALIZED,
  REFRESH_ACCOUNT_HIERARCHY,
  SET_GLOBAL_GROUP,
  SOLOMON_ID,
  TOGGLE_GLOBAL_GROUP_CHOOSER
} from '../actions/global-group-chooser';
import {APPLICATION_INITIALIZATION_COMPLETE} from '../actions/initialization';

export interface GlobalGroupState {
  groupChooser: {
    isViewing: boolean;
  };
  accountHierarchy: AccountLink[];
  globalGroup: AccountLink;
  globalGroupId: number;
  globalGroupHierarchy: AccountLink[];
  solomonId: string;
  isInitialized: boolean;
}

// defines the initial state for the reducer ...
export const initialState: GlobalGroupState = {
  groupChooser : {
    isViewing : false
  },
  accountHierarchy     : null,
  globalGroup          : null,
  globalGroupId        : null,
  globalGroupHierarchy : null,
  solomonId            : null,
  isInitialized        : false
};

// defines this reducers reducer functions ...
const reducers = {
  [TOGGLE_GLOBAL_GROUP_CHOOSER] : ({groupChooser}: GlobalGroupState, {value}) => {
    groupChooser.isViewing = value;
  },
  [APPLICATION_INITIALIZATION_COMPLETE] : (
    draft: GlobalGroupState, {accounts, user}
  ) => {
    const accountsMap = toAccountIdMap(accounts.concat({...user.account, subAccounts: accounts}));
    const hierarchy = [
      {
        ...user.account,
        subAccounts : sortNestedAccounts({accounts: cloneDeep(accounts)})
      }
    ];
    const account           = hierarchy[0];
    draft.accountHierarchy  = hierarchy;
    draft.globalGroup       = account;
    draft.globalGroupId     = entityId(account);
    const selectedAccount   = accountsMap[entityId(account)];
    draft.globalGroupHierarchy = [selectedAccount];
  },
  [SET_GLOBAL_GROUP] : (draft: GlobalGroupState, {group}) => {
    const groupId                = entityId(group);
    draft.globalGroup            = group;
    draft.globalGroupId          = groupId;
    draft.groupChooser.isViewing = false;
    const accountsMap            = toAccountIdMap(draft.accountHierarchy);
    const selectedAccount        = accountsMap[groupId];
    draft.globalGroupHierarchy   = [selectedAccount];
  },
  [REFRESH_ACCOUNT_HIERARCHY] : (draft: GlobalGroupState, {accounts}) => {
    draft.accountHierarchy[0].subAccounts = sortNestedAccounts({accounts: cloneDeep(accounts)});
  },
  [SOLOMON_ID] : (draft: GlobalGroupState, {solomonId}) => {
    draft.solomonId = solomonId;
  },
  [GLOBAL_GROUP_INITIALIZED] : (draft: GlobalGroupState) => {
    draft.isInitialized = true;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: GlobalGroupState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
